import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function InstructorLogin() {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    businessName: ''
  });

  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(credentials.email, credentials.password);
      navigate('/dashboard');
    } catch (error) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (signupData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Create Firebase account directly - no payment required
      await signup(signupData.email, signupData.password, {
        firstName: signupData.firstName,
        lastName: signupData.lastName,
        businessName: signupData.businessName,
        accountType: 'instructor'
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating account:', error);
      setError(error.message || 'Error creating account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-klorah-dark">
      <div className="w-full max-w-md">
        <div className="glass-panel-strong p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2 text-white">
              {isSignup ? 'Create Instructor Account' : 'Instructor Login'}
            </h2>
            <p className="text-sm text-klorah-text">
              {isSignup ? 'Start creating amazing workshops' : 'Access your workshop dashboard'}
            </p>
          </div>

          {isSignup ? (
            <form onSubmit={handleSignupSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-klorah-text">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={signupData.firstName}
                    onChange={(e) => setSignupData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-4 py-3 bg-klorah-card border border-klorah-border rounded-xl text-white placeholder-klorah-text-light focus:border-klorah-primary focus:ring-2 focus:ring-klorah-primary/20 transition-all outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={signupData.lastName}
                    onChange={(e) => setSignupData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Business Name
                </label>
                <input
                  type="text"
                  value={signupData.businessName}
                  onChange={(e) => setSignupData(prev => ({ ...prev, businessName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                  placeholder="Your Photography Business"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={signupData.email}
                  onChange={(e) => setSignupData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  value={signupData.password}
                  onChange={(e) => setSignupData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-blue-500 bg-white"
                  required
                />
              </div>

              {error && (
                <div className="p-3 text-sm bg-red-50 text-red-600 border border-red-200 rounded">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full klorah-button disabled:opacity-50"
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-klorah-text">
                  Email
                </label>
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-klorah-card border border-klorah-border rounded-xl text-white placeholder-klorah-text-light focus:border-klorah-primary focus:ring-2 focus:ring-klorah-primary/20 transition-all outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-klorah-text">
                  Password
                </label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 bg-klorah-card border border-klorah-border rounded-xl text-white placeholder-klorah-text-light focus:border-klorah-primary focus:ring-2 focus:ring-klorah-primary/20 transition-all outline-none"
                  required
                />
              </div>

              {error && (
                <div className="p-3 text-sm bg-red-500/10 text-red-400 border border-red-500/30 rounded-xl">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full klorah-button disabled:opacity-50"
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsSignup(!isSignup)}
              className="text-sm text-klorah-primary hover:text-klorah-secondary transition-colors"
            >
              {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InstructorLogin; 